import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { BaseModal } from '@repo/components/modals';
import Button from '@components/Button';
import styles from './TwitterModal.module.scss';
import { useSelector } from 'react-redux';
import { selectIntegrationStatusOnDevice } from '@repo/redux/reducers/user/integrations';
import { syncDeviceIntegration } from '@repo/redux/actions/deviceIntegrations';
import { FriendType } from '@repo/utils/models/Integration';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { LoaderIcon } from '@repo/icons';

interface TwitterModalProps {
  onClose: () => void;
  forceDarkMode?: boolean;
}

type SetupStatus = 'waitingForUser' | 'waitingForTwitter' | 'syncing' | 'complete';

const labelForFriendType = (friendType: FriendType) => {
  switch (friendType) {
    case 'all':
      return 'Everyone';
    case 'mutual':
      return 'Mutual follows';
    case 'followers':
      return 'Who follows you';
    case 'following':
      return "Who you're following";
  }
};

const SelectFriendType = ({ friendType, setFriendType }) => (
  <div>
    <p>
      We will seamlessly sync your Twitter connections to Clay in one easy step.{' '}
      <a href="https://go.clay.earth/twitter" target="_blank">
        Learn more
      </a>
      .
    </p>
    <p>Please select which connections you would like to sync.</p>
    <div className={styles.toggles}>
      {['all', 'mutual', 'followers', 'following'].map((ft) => (
        <React.Fragment key={ft}>
          <input
            type="radio"
            id={ft}
            name="friendType"
            value={ft}
            checked={ft === friendType}
            onChange={() => setFriendType(ft)}
          />
          <label htmlFor={ft}>{labelForFriendType(ft as FriendType)}</label>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export default function TwitterModal({ onClose, forceDarkMode }: TwitterModalProps) {
  const { enabled: userDidAuthorizeTwitter, syncing } = useSelector(
    selectIntegrationStatusOnDevice('twitter-platforms'),
  ) || { enabled: false, syncing: false };

  // i don't think there should be an existing integration if this modal is being shown,
  // but if there is -- use the integration friend type to set the default state
  const [friendType, setFriendType] = useState<FriendType>('mutual');
  const [status, setStatus] = useState<SetupStatus>('waitingForUser');

  const connect = () => {
    setStatus('waitingForTwitter');
    syncDeviceIntegration('twitter-platforms', { friendType });
  };

  useEffect(() => {
    if (userDidAuthorizeTwitter && syncing && status !== 'syncing') {
      setStatus('syncing');
    } else if (userDidAuthorizeTwitter && !syncing && status === 'syncing') {
      setStatus('complete');
    }
  }, [syncing, userDidAuthorizeTwitter, status]);

  return (
    <BaseModal
      className="modal-small"
      onRequestClose={onClose}
      forceDarkMode={forceDarkMode}
      isOpen
    >
      <div className="modal-header">
        <StateBasedFadeTransition
          state={status}
          elementByStateMap={{
            waitingForUser: <span>Allow access to your Twitter Account</span>,
            waitingForTwitter: <span>Waiting for Twitter</span>,
            syncing: <span>Importing Connections&hellip;</span>,
            complete: <span>Successfully Imported Connections</span>,
          }}
        />
      </div>
      <div className={cn(styles.modal, 'modal-content-padded')}>
        <StateBasedFadeTransition
          state={status}
          elementByStateMap={{
            waitingForUser: (
              <SelectFriendType friendType={friendType} setFriendType={setFriendType} />
            ),
            waitingForTwitter: (
              <p>Please sign into Twitter to allow access to your connections...</p>
            ),
            syncing: (
              <p>
                Your connections are now being imported. Please keep the Clay app open until the
                import is complete. You may close this modal.
              </p>
            ),
            complete: (
              <div>
                <p>
                  Your connections have been imported from Twitter. It may take a few minutes before
                  you start seeing them in Clay.
                </p>
                <p>You can now close this window.</p>
              </div>
            ),
          }}
        />
        <StateBasedFadeTransition
          state={status}
          elementByStateMap={{
            waitingForUser: (
              <div className={styles.btnRow}>
                <Button className="btn btn-muted" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  className="btn btn-primary"
                  disabled={['waiting', 'syncing'].includes(status)}
                  onClick={connect}
                >
                  Connect
                </Button>
              </div>
            ),
            waitingForTwitter: (
              <div className={styles.btnRow}>
                <LoaderIcon />
              </div>
            ),
            syncing: (
              <div className={styles.btnRow}>
                <Button className="btn btn-primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            ),
            complete: (
              <div className={styles.btnRow}>
                <Button className="btn btn-primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            ),
          }}
        />
      </div>
    </BaseModal>
  );
}
